import { useAuth } from "@/hooks/useAuth";
import { Role } from "@/enums/role";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appActions } from "@/redux/actions/app.actions";
import { useDispatch } from "@/redux/store";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import type { FeatureFlagKey } from "@rebuild/shared";

interface OrgAdminRouteWrapperProps {
  children: ReactNode;
  /** Optional additional flag that must also be on for this specific page (e.g. `MANAGE_OFFICES_ENABLED`). */
  requireFlag?: FeatureFlagKey;
  /** Where to bounce when `requireFlag` is off. Defaults to the admin landing (`/settings/users`). */
  flagOffRedirect?: string;
}

/** Customer org admin self-serve (`admin`) plus Rebuild `internal` operators. Do not use for legacy `/admin` (creator + admin). */
export const OrgAdminRouteWrapper = ({
  children,
  requireFlag,
  flagOffRedirect = "/settings/users",
}: OrgAdminRouteWrapperProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { isFeatureEnabled, loading: featureFlagsLoading } = useFeatureFlags();
  const selfServeEnabled = isFeatureEnabled("SELF_SERVE_ADMIN_ENABLED");
  const additionalFlagEnabled = requireFlag ? isFeatureEnabled(requireFlag) : true;
  const isAllowedRole = user?.role === Role.ADMIN || user?.role === Role.INTERNAL;

  useEffect(() => {
    if (!isAuthenticated) {
      const fullUrl = `${location.pathname}${location.search}${location.hash}`;
      dispatch(appActions.setAppState({ redirectTo: fullUrl }));
      navigate("/");
      return;
    }

    if (!isAllowedRole) {
      navigate("/dashboard");
      return;
    }

    // Customer admins are gated by the SELF_SERVE_ADMIN_ENABLED flag on their org.
    // Wait for the flag manager to hydrate before redirecting so we don't bounce on a stale `false`.
    if (!featureFlagsLoading && !selfServeEnabled) {
      navigate("/dashboard");
      return;
    }

    // Per-page additional flag (e.g. MANAGE_OFFICES_ENABLED) — bounce to the admin
    // landing rather than the dashboard, since the user has already cleared the admin gate.
    if (!featureFlagsLoading && !additionalFlagEnabled) {
      navigate(flagOffRedirect);
    }
  }, [
    navigate,
    isAuthenticated,
    dispatch,
    location,
    isAllowedRole,
    featureFlagsLoading,
    selfServeEnabled,
    additionalFlagEnabled,
    flagOffRedirect,
  ]);

  if (!isAuthenticated || !user) return null;
  if (!isAllowedRole) return null;
  if (!featureFlagsLoading && !selfServeEnabled) return null;
  if (!featureFlagsLoading && !additionalFlagEnabled) return null;

  return <>{children}</>;
};
